"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { getKeyGrantFn } from "~/lib/functions/encrypted-data";

import {
  base64UrlToBuffer,
  decryptData,
  unwrapDek,
  type EncryptedData,
} from "./crypto";
import type { KeyManager } from "./key-manager";

/**
 * Describes a single field that may or may not be encrypted.
 * If `encrypted` is present, it will be decrypted; otherwise `plaintext` is used.
 */
export interface FieldSpec {
  /** JSON-serialized EncryptedData from the DB encrypted column */
  encrypted: string | null | undefined;
  /** Plaintext fallback value (pre-migration). Born-encrypted types omit it. */
  plaintext?: string | null;
}

/** Record mapping field names to their decrypted string values. */
export type DecryptedFields<K extends string> = Record<K, string | null>;

/** Function type for fetching a key grant (DEK) for a resource. */
export type GetKeyGrantFn = (params: {
  data: { resourceType: string; resourceId: string };
}) => Promise<string | null>;

interface UseDecryptedResourceParams<K extends string> {
  /** Resource type for key grant lookup (e.g., "overallVision", "goal") */
  resourceType: string;
  /** Resource ID for key grant lookup */
  resourceId: string | undefined;
  /** Unlocked KeyManager (null if locked) */
  keyManager: KeyManager | null;
  /** Map of field names to their encrypted/plaintext specs */
  fields: Record<K, FieldSpec>;
  /** Override key grant fetcher (defaults to user getKeyGrantFn). */
  getKeyGrant?: GetKeyGrantFn;
}

interface UseDecryptedResourceResult<K extends string> {
  /** Decrypted (or plaintext-fallback) values for each field */
  data: DecryptedFields<K>;
  /** Whether decryption is in progress */
  isDecrypting: boolean;
}

/**
 * Decrypts the fields of one resource with its single DEK. A field without
 * ciphertext, or one that fails to decrypt, falls back to its plaintext.
 * Throws when the grant cannot be fetched or unwrapped.
 */
export async function decryptResourceFields<K extends string>({
  resourceType,
  resourceId,
  keyManager,
  fields,
  getKeyGrant = getKeyGrantFn,
}: {
  resourceType: string;
  resourceId: string;
  keyManager: KeyManager;
  fields: Record<K, FieldSpec>;
  getKeyGrant?: GetKeyGrantFn;
}): Promise<DecryptedFields<K>> {
  const entries = Object.entries(fields) as Array<[K, FieldSpec]>;
  const plaintextValues = {} as DecryptedFields<K>;
  for (const [key, spec] of entries) {
    plaintextValues[key] = spec.plaintext ?? null;
  }
  if (!entries.some(([, spec]) => !!spec.encrypted)) return plaintextValues;

  const wrappedDekJson = await getKeyGrant({
    data: { resourceType, resourceId },
  });
  if (!wrappedDekJson) return plaintextValues;

  const wrappedDek = JSON.parse(wrappedDekJson);
  const ephemeralPubKey = await crypto.subtle.importKey(
    "raw",
    base64UrlToBuffer(wrappedDek.ephemeralPublicKey),
    { name: "ECDH", namedCurve: "P-256" },
    true,
    [],
  );
  const dek = await unwrapDek(
    wrappedDek,
    ephemeralPubKey,
    keyManager.getPrivateKey(),
  );

  const decryptedEntries = await Promise.all(
    entries.map(async ([key, spec]): Promise<[K, string | null]> => {
      if (!spec.encrypted) return [key, spec.plaintext ?? null];
      try {
        const encryptedData: EncryptedData = JSON.parse(spec.encrypted);
        const decryptedBytes = await decryptData(encryptedData, dek);
        return [key, new TextDecoder().decode(decryptedBytes)];
      } catch (fieldErr) {
        console.warn("Field decryption failed, falling back to plaintext", {
          resourceType,
          resourceId,
          fieldName: key,
          error: fieldErr,
        });
        return [key, spec.plaintext ?? null];
      }
    }),
  );

  const result = {} as DecryptedFields<K>;
  for (const [key, value] of decryptedEntries) {
    result[key] = value;
  }
  return result;
}

/**
 * Hook to decrypt multiple fields of a resource using a single DEK.
 *
 * Fetches the wrapped DEK once, unwraps it, then decrypts all fields in parallel.
 * Falls back to plaintext for any field without encrypted data.
 *
 * @example
 * ```tsx
 * const { data, isDecrypting } = useDecryptedResource({
 *   resourceType: "goal",
 *   resourceId: goal.id,
 *   keyManager,
 *   fields: {
 *     title: { encrypted: goal.encryptedTitle, plaintext: goal.title },
 *     description: { encrypted: goal.encryptedDescription, plaintext: goal.description },
 *   },
 * });
 * // data.title, data.description
 * ```
 */
export function useDecryptedResource<K extends string>({
  resourceType,
  resourceId,
  keyManager,
  fields,
  getKeyGrant,
}: UseDecryptedResourceParams<K>): UseDecryptedResourceResult<K> {
  const fieldEntries = Object.entries(fields) as Array<[K, FieldSpec]>;
  const hasAnyEncrypted = fieldEntries.some(([, spec]) => !!spec.encrypted);

  // Stable fingerprint of field values so the effect only re-runs when content changes,
  // not when the parent creates a new fields object with the same values.
  const fieldsFingerprint = useMemo(() => {
    return fieldEntries
      .map(
        ([key, spec]) =>
          `${key}:${spec.encrypted ?? ""}|${spec.plaintext ?? ""}`,
      )
      .join(";");
  }, [
    fieldEntries
      .map(([, s]) => `${s.encrypted ?? ""}|${s.plaintext ?? ""}`)
      .join(";"),
  ]);

  // Build plaintext fallback — stable as long as field values don't change
  const plaintextValues = useMemo(() => {
    const result = {} as DecryptedFields<K>;
    for (const [key, spec] of fieldEntries) {
      result[key] = spec.plaintext ?? null;
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsFingerprint]);

  const [data, setData] = useState<DecryptedFields<K>>(plaintextValues);
  // Decrypting is derived from which request last settled, not set by the
  // effect, so no render shows "done" between new ciphertext (or an id that
  // just arrived) and the effect starting. A form that reads its initial
  // values once can wait on it without ever seeing the empty fallback.
  const requestKey =
    hasAnyEncrypted && keyManager && resourceId
      ? `${resourceType}|${resourceId}|${fieldsFingerprint}`
      : null;
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const isDecrypting = requestKey !== null && settledKey !== requestKey;

  // Track the latest params via ref so the async closure always reads current values
  const latestRef = useRef({
    resourceType,
    resourceId,
    keyManager,
    fields,
    getKeyGrant,
  });
  latestRef.current = {
    resourceType,
    resourceId,
    keyManager,
    fields,
    getKeyGrant,
  };

  useEffect(() => {
    // No encrypted data or no key manager — use plaintext directly
    if (!hasAnyEncrypted || !keyManager || !resourceId) {
      setData(plaintextValues);
      return;
    }

    // Capture narrowed values for the async closure
    const id = resourceId;
    const km = keyManager;

    const key = requestKey;
    let cancelled = false;

    async function decryptAll(): Promise<void> {
      try {
        const result = await decryptResourceFields({
          resourceType,
          resourceId: id,
          keyManager: km,
          fields: latestRef.current.fields,
          getKeyGrant: latestRef.current.getKeyGrant,
        });
        if (!cancelled) setData(result);
      } catch (err) {
        console.error("Failed to decrypt resource:", err);
        if (!cancelled) setData(plaintextValues);
      } finally {
        if (!cancelled) setSettledKey(key);
      }
    }

    decryptAll();
    return () => {
      cancelled = true;
    };
    // fieldsFingerprint tracks actual content changes without unstable object references
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasAnyEncrypted,
    keyManager,
    resourceType,
    resourceId,
    fieldsFingerprint,
    plaintextValues,
  ]);

  return { data, isDecrypting };
}
